import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { CreateNoteRequest, NoteTargetType, QuickCaptureSuggestEntity } from '@leanopx/shared';
import { useAuth } from '@/components/providers/auth-provider';
import { api } from '@/lib/api';
import { logger } from '@/lib/logger';
import { toast } from '@/lib/toast';
import { t } from '@/lib/i18n';
import { QuickCapture } from './quick-capture';

/** Minimum draft length before we ask the server for suggestions. Mirrors the server's `text.min(3)`. */
const QC_SUGGEST_MIN_CHARS = 3;
/** Debounce window for the suggest API. 300ms matches a typist's pause-between-words. */
const QC_SUGGEST_DEBOUNCE_MS = 300;

/**
 * QuickCaptureProvider — global SlideOver for "drop a thought from
 * anywhere in the portal." Bound to ⌘N at the app-shell level (see
 * `app-shell.tsx`); composed against the design-system
 * `<SlideOver emphasis="accent" scrim="transparent">` primitive so the
 * dossier behind stays readable while the user captures.
 *
 * The provider owns:
 *   - open/close state
 *   - the in-progress draft (body text + linked entities; attachments
 *     are stubbed for v1 — the UI shows the toolbar but uploads land
 *     in a follow-up alongside the editor)
 *   - the save handler: calls `api.createNote(orgId, ...)` and emits a
 *     client-side `note.created` audit event. The server's POST handler
 *     ALSO emits a `note.create` audit (`apps/functions/src/http/router.ts`
 *     line ~3117) — the two events bookend each other (intent + outcome).
 *
 * Notes:
 *   - Multi-tenant: orgId comes from `useAuth().activeOrg`. If there's no
 *     active org (shouldn't happen behind requireAuth, but defend), the
 *     CTA is disabled and we surface an error toast.
 *   - Attachments toolbar (Voice/Photo/File/Sketch/Mention/Tag) is wired
 *     into the UI surface but the actual upload flow shares plumbing with
 *     the dossier-notes editor and gets reused in a follow-up. The UI
 *     shows a help microcopy and the buttons are inert.
 */

/**
 * Entity context the user is "thinking about" when they open quick-
 * capture from a dossier — wired through to the SlideOver header
 * ("· thinking about Acme Foundry") and pre-attached as the first
 * `NoteTarget` on save.
 */
export interface QuickCaptureEntityContext {
  kind: NoteTargetType;
  /** Stable entity id (e.g. dossier slug). */
  key: string;
  /** Display name for the header eyebrow + initial entity pill. */
  name: string;
}

export interface QuickCaptureContextValue {
  isOpen: boolean;
  /** Open the panel with no entity context. */
  open: () => void;
  /** Open the panel pre-attached to an entity. */
  openWith: (entity: QuickCaptureEntityContext) => void;
  close: () => void;
  /** Latest agent-suggested entities for the current draft. Empty until the user types ≥3 chars. */
  suggestions: QuickCaptureSuggestEntity[];
  /** True while a suggest call is in flight. UI may render a quiet spinner. */
  suggestionsLoading: boolean;
}

const QuickCaptureCtx = createContext<QuickCaptureContextValue | null>(null);

/**
 * Hook for triggering quick-capture from anywhere in the portal.
 *
 * @example
 * ```tsx
 * const { open, openWith } = useQuickCapture();
 * // From the topbar Capture button:
 * <button onClick={open}>Capture</button>
 * // From a dossier header:
 * <button onClick={() => openWith({ kind: 'customer', key: 'acme-corp', name: 'Acme Foundry' })}>
 *   Add note
 * </button>
 * ```
 */
export function useQuickCapture(): QuickCaptureContextValue {
  const ctx = useContext(QuickCaptureCtx);
  if (!ctx) {
    throw new Error('useQuickCapture must be used inside <QuickCaptureProvider>');
  }
  return ctx;
}

/**
 * Save payload assembled from draft + active context. Kept narrow so
 * the SlideOver UI doesn't need to know about API shapes.
 */
export interface QuickCaptureSavePayload {
  body: string;
  /** Linked entities (the context entity, if any, lives at index 0). */
  targets: QuickCaptureEntityContext[];
}

/**
 * Props the SlideOver component consumes. Decouples the dumb-component
 * surface from the provider's internals.
 */
export interface QuickCaptureViewProps {
  open: boolean;
  context: QuickCaptureEntityContext | null;
  body: string;
  onBodyChange: (body: string) => void;
  targets: QuickCaptureEntityContext[];
  onRemoveTarget: (entityKey: string) => void;
  onClose: () => void;
  onDiscard: () => void;
  onSave: () => void;
  saving: boolean;
  saveDisabled: boolean;
  /** Agent-suggested entities for the current draft. */
  suggestions: QuickCaptureSuggestEntity[];
  /** True while a suggest call is in flight. */
  suggestionsLoading: boolean;
  /** "+ Add all N" — appends every suggestion to the targets list. */
  onAddAllSuggestions: () => void;
  /** Append a single suggestion to targets. */
  onAddSuggestion: (suggestion: QuickCaptureSuggestEntity) => void;
}

/**
 * The provider. Renders the SlideOver inside its own subtree so the
 * Radix Portal mounts under the right theme provider.
 *
 * Wrap once at the app-shell level — there's only one quick-capture
 * surface per app, owned globally.
 */
export function QuickCaptureProvider({ children }: { children: ReactNode }) {
  const { activeOrg } = useAuth();
  const orgId = activeOrg?.id ?? null;

  const [isOpen, setIsOpen] = useState(false);
  const [context, setContext] = useState<QuickCaptureEntityContext | null>(null);
  const [body, setBody] = useState('');
  const [targets, setTargets] = useState<QuickCaptureEntityContext[]>([]);
  const [saving, setSaving] = useState(false);
  const [suggestions, setSuggestions] = useState<QuickCaptureSuggestEntity[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  // Monotonic request id — late responses from an earlier debounce
  // burst would otherwise overwrite the latest set as the user keeps
  // typing. Compare against the captured id on resolution.
  const suggestRequestIdRef = useRef(0);

  // Deferred-reset timer handle. We delay `resetDraft` past the slide-
  // out animation so the text doesn't visibly wipe on the way out — but
  // if the provider unmounts (user navigates away) inside that 200ms
  // window the timer would fire setState on an unmounted tree. Stash
  // the handle so the cleanup effect below can cancel it.
  const resetTimerRef = useRef<number | null>(null);
  useEffect(() => {
    return () => {
      if (resetTimerRef.current !== null) {
        window.clearTimeout(resetTimerRef.current);
        resetTimerRef.current = null;
      }
    };
  }, []);

  const resetDraft = useCallback(() => {
    setBody('');
    setTargets([]);
    setContext(null);
    setSuggestions([]);
    setSuggestionsLoading(false);
    // Bump the request-id so any in-flight suggest call's response is
    // discarded on resolution.
    suggestRequestIdRef.current += 1;
  }, []);

  const open = useCallback(() => {
    setContext(null);
    setTargets([]);
    setIsOpen(true);
  }, []);

  const openWith = useCallback((entity: QuickCaptureEntityContext) => {
    setContext(entity);
    setTargets([entity]);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    // Defer the draft reset so the slide-out animation doesn't visibly
    // wipe the text on the way out. Clear any prior pending reset first
    // so a close→reopen→close inside 200ms doesn't queue up two resets
    // (and the cleanup effect above cancels whatever's pending on
    // unmount, so the timer can't fire after the provider is gone).
    if (resetTimerRef.current !== null) {
      window.clearTimeout(resetTimerRef.current);
    }
    resetTimerRef.current = window.setTimeout(() => {
      resetTimerRef.current = null;
      resetDraft();
    }, 200);
  }, [resetDraft]);

  const handleRemoveTarget = useCallback((entityKey: string) => {
    setTargets((prev) => prev.filter((target) => target.key !== entityKey));
  }, []);

  const handleAddSuggestion = useCallback((suggestion: QuickCaptureSuggestEntity) => {
    setTargets((prev) => {
      if (prev.some((target) => target.key === suggestion.key && target.kind === suggestion.kind)) {
        return prev;
      }
      return [
        ...prev,
        {
          kind: suggestion.kind,
          key: suggestion.key,
          name: suggestion.label,
        },
      ];
    });
  }, []);

  const handleAddAllSuggestions = useCallback(() => {
    setTargets((prev) => {
      const present = new Set(prev.map((target) => `${target.kind}::${target.key}`));
      const merged = [...prev];
      for (const s of suggestions) {
        const key = `${s.kind}::${s.key}`;
        if (present.has(key)) continue;
        present.add(key);
        merged.push({ kind: s.kind, key: s.key, name: s.label });
      }
      return merged;
    });
  }, [suggestions]);

  // Debounced suggest call. Fires whenever the draft body crosses the
  // 3-char threshold and stays at-or-above it; auto-clears suggestions
  // when the draft falls back below the threshold or the panel closes.
  // Late responses are dropped via the monotonic request-id ref so a
  // slow N-1 response can't overwrite a fast N response.
  useEffect(() => {
    if (!isOpen) return;
    if (!orgId) return;

    const trimmed = body.trim();
    if (trimmed.length < QC_SUGGEST_MIN_CHARS) {
      // Pre-threshold — clear any stale suggestions silently. Bumping the
      // ref invalidates any in-flight call started by a previous burst.
      suggestRequestIdRef.current += 1;
      setSuggestions([]);
      setSuggestionsLoading(false);
      return;
    }

    const requestId = ++suggestRequestIdRef.current;
    const handle = window.setTimeout(() => {
      setSuggestionsLoading(true);
      const requestBody = {
        text: trimmed,
        ...(context ? { contextEntity: { kind: context.kind, key: context.key } } : {}),
      };
      api
        .quickCaptureSuggest(orgId, requestBody)
        .then((response) => {
          if (requestId !== suggestRequestIdRef.current) return;
          setSuggestions(response.suggestions);
        })
        .catch((err: unknown) => {
          if (requestId !== suggestRequestIdRef.current) return;
          // Suggest is a quiet, non-blocking enhancement; surface to
          // the dev console but don't toast — a flaky debounced call
          // shouldn't interrupt the user's typing.
          logger.debug('quick-capture.suggest.failed', {
            err: err instanceof Error ? err.message : String(err),
          });
          setSuggestions([]);
        })
        .finally(() => {
          if (requestId !== suggestRequestIdRef.current) return;
          setSuggestionsLoading(false);
        });
    }, QC_SUGGEST_DEBOUNCE_MS);

    return () => window.clearTimeout(handle);
  }, [body, context, isOpen, orgId]);

  const handleSave = useCallback(async () => {
    if (!orgId) {
      toast.error(t('quickCapture.errorNoOrg'));
      return;
    }
    const trimmed = body.trim();
    if (trimmed.length === 0 && targets.length === 0) {
      // Empty note with no targets — close without saving.
      close();
      return;
    }
    setSaving(true);
    try {
      const noteBody: CreateNoteRequest = {
        body: trimmed,
        targets: targets.map((target) => ({
          type: target.kind,
          entityId: target.key,
          entitySnapshotName: target.name,
        })),
        tags: [],
        attachments: [],
      };
      // `api.createNote` (`POST /orgs/:orgId/notes`) — the server stamps
      // id / author / timestamps and recomputes `targetEntityIds`.
      // Attachments wire in once the rich-text editor lands here.
      const created = await api.createNote(orgId, noteBody);
      // Client-side bookend audit. The server already emitted `note.create`
      // (apps/functions/src/http/router.ts line ~3117); this `note.created`
      // client event records the user-intent side of the same mutation —
      // the per-action policy lives in `CLIENT_AUDIT_POLICY` and surfaces
      // in the user's own audit feed.
      try {
        await api.recordAuditEventBatch([
          {
            action: 'note.created',
            target: created.id,
            detail: {
              source: 'quick-capture',
              targetCount: targets.length,
              hasBody: trimmed.length > 0,
            },
          },
        ]);
      } catch {
        // Audit batch is best-effort from the client — the server
        // already wrote its own canonical event.
      }
      toast.success(t('quickCapture.saved'));
      close();
    } catch (err) {
      // External API plumbing is allowed to throw; surface to the user
      // rather than swallowing per CLAUDE.md "no fallback data" rule.
      const message = err instanceof Error ? err.message : t('quickCapture.errorSave');
      toast.error(message);
    } finally {
      setSaving(false);
    }
  }, [body, close, orgId, targets]);

  const value = useMemo<QuickCaptureContextValue>(
    () => ({
      isOpen,
      open,
      openWith,
      close,
      suggestions,
      suggestionsLoading,
    }),
    [isOpen, open, openWith, close, suggestions, suggestionsLoading],
  );

  const trimmedLen = body.trim().length;
  const saveDisabled = saving || (trimmedLen === 0 && targets.length === 0) || !orgId;

  return (
    <QuickCaptureCtx.Provider value={value}>
      {children}
      <QuickCapture
        open={isOpen}
        context={context}
        body={body}
        onBodyChange={setBody}
        targets={targets}
        onRemoveTarget={handleRemoveTarget}
        onClose={close}
        onDiscard={close}
        onSave={() => void handleSave()}
        saving={saving}
        saveDisabled={saveDisabled}
        suggestions={suggestions}
        suggestionsLoading={suggestionsLoading}
        onAddAllSuggestions={handleAddAllSuggestions}
        onAddSuggestion={handleAddSuggestion}
      />
    </QuickCaptureCtx.Provider>
  );
}
